import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { collection, query, orderBy, getDocs, getDoc, doc, updateDoc, arrayUnion, arrayRemove, serverTimestamp, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { ShowCard } from '../../components/ShowCard';
import { useWatchlist } from '../../context/WatchlistContext';
import { TipsShowReview } from './TipsShowReview';
import { RatingOnlyModal } from '../../components/RatingOnlyModal';
import { useAuth } from '../../context/AuthContext';
import { StreamingSourceModal } from '../../components/StreamingSourceModal/StreamingSourceModal';
import { Toast } from '../../components/Toast/Toast';
import './Tips.css';
import { getShowStatus } from '../../utils/showUtils';
import { 
  addToWatchlist, 
  removeFromWatchlist, 
  markShowAsSeen, 
  archiveShow, 
  unarchiveShow,
  getSeenShows,
  getArchivedShows,
  markShowAsWatching,
  addActivity,
  getUserLists
} from '../../services/databaseService';
import { pageStateStore } from '../../services/pageStateStore';
import { createPortal } from 'react-dom';

const TOAST_DURATION = 3000; // 3 seconds

const SHOW_STATUSES = {
  WATCHLIST: 'watchlist',
  WATCHING: 'watching',
  SEEN: 'seen',
  ARCHIVED: 'archived',
  NONE: 'none'
};

function TipsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get sourceId from both location state and pageState
  const sourceId = location.state?.sourceId || pageStateStore.getState('tips')?.sourceId;
  const [selectedSource, setSelectedSource] = useState(null);
  const [activeStreamingFilter, setActiveStreamingFilter] = useState('all');
  const [tipsList, setTipsList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { watchlist } = useWatchlist();
  const [seenList, setSeenList] = useState([]);
  const [availableStreamingSources, setAvailableStreamingSources] = useState(new Set(['all']));
  const [showRatingOnlyModal, setShowRatingOnlyModal] = useState(false);
  const [selectedShowForRating, setSelectedShowForRating] = useState(null);
  const { user } = useAuth();
  const [loadingItems, setLoadingItems] = useState(new Set());
  const [toastState, setToastState] = useState({ 
    show: false, 
    message: '', 
    showSpinner: false 
  });
  const [showStreamingModal, setShowStreamingModal] = useState(false);
  const [selectedShowForSources, setSelectedShowForSources] = useState(null);
  const [activeStatusFilter, setActiveStatusFilter] = useState('none');
  const [archivedList, setArchivedList] = useState([]);
  const [scrollPosition, setScrollPosition] = useState(() => {
    return location.state?.scrollPosition || 0;
  });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [streamingSources, setStreamingSources] = useState({});
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [selectedShowForStatus, setSelectedShowForStatus] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ left: 0, top: 0 });
  const [toast, setToast] = useState(null);
  const [userLists, setUserLists] = useState({ 
    watchlist: [], 
    seenList: [], 
    watchingList: [], 
    archivedList: [] 
  });
  const [listsLoading, setListsLoading] = useState(true);
  const [currentShowStatus, setCurrentShowStatus] = useState(SHOW_STATUSES.NONE);

  useEffect(() => {
    if (!sourceId) {
      navigate('/tips/pre', { replace: true });
      return () => {}; // Clean return for unmounting
    }
  }, [sourceId, navigate]);

  // Fetch source name
  useEffect(() => {
    const fetchSourceName = async () => {
      if (!sourceId) return;
      
      try {
        const sourceDoc = await getDoc(doc(db, 'TipsLists', sourceId));
        if (sourceDoc.exists()) {
          setSelectedSource(sourceDoc.data().source || 'Unknown Source');
        }
      } catch (error) {
        console.error('Error fetching source name:', error);
      }
    };

    fetchSourceName();
  }, [sourceId]);

  // Fetch tips
  useEffect(() => {
    const fetchTips = async () => {
      if (!sourceId) return;

      try {
        // Check if we have cached data
        const cachedKey = `tips_${sourceId}`;
        const cached = sessionStorage.getItem(cachedKey);
        if (cached) {
          const { shows, timestamp } = JSON.parse(cached);
          const now = Date.now();
          if (now - timestamp < 5 * 60 * 1000) { // 5 minute cache
            setTipsList(shows);
            setLoading(false);
            return;
          }
        }

        setLoading(true);
        const tipsDoc = await getDoc(doc(db, 'TipsLists', sourceId));
        
        if (tipsDoc.exists() && tipsDoc.data().shows) {
          const formattedShows = tipsDoc.data().shows.map(show => ({
            id: show.imdb_id,
            firestore_id: show.id,
            watchmode_id: show.watchmode_id,
            title: show.title,
            poster: (show.poster && show.poster !== 'N/A') ? show.poster : '/images/default-poster.jpg',
            year: show.year,
            total_seasons: show.total_seasons,
            averageRating: show.averageRating,
            ratingCount: show.ratingCount,
            streamingSources: show.streamingSources || [],
            streaming_source: show.streaming_source || '',
            date: show.date || '',
            plot_overview: show.plot_overview,
            backdrop: show.backdrop,
            imdb_id: show.imdb_id,
            actors: show.actors || [],
            creators: show.creators || [],
            review: show.review || '',
            reviewer: show.reviewer || '',
            reviewDate: show.reviewDate || null
          }));
          
          const allStreamingSources = new Set(['all']);
          formattedShows.forEach(show => {
            if (show.streamingSources) {
              show.streamingSources.forEach(source => {
                allStreamingSources.add(source.toLowerCase());
              });
            }
          });
          
          // Cache the results
          sessionStorage.setItem(cachedKey, JSON.stringify({
            shows: formattedShows,
            timestamp: Date.now()
          }));
          
          setTipsList(formattedShows);
          setAvailableStreamingSources(allStreamingSources);
        }
      } catch (err) {
        console.error('Error in fetchTips:', err);
        setError('Failed to load tips');
      } finally {
        setLoading(false);
      }
    };

    fetchTips();
  }, [sourceId]);

  // Modify scroll handling effect
  useEffect(() => {
    const handleScroll = () => {
      const position = window.scrollY;
      pageStateStore.setState('tips', { 
        scrollPosition: position,
        sourceId
      });
    };

    window.addEventListener('scroll', handleScroll);

    // Initial scroll restore
    if (!loading && tipsList) {
      const savedPosition = pageStateStore.getState('tips')?.scrollPosition || 0;
      if (savedPosition > 0) {
        requestAnimationFrame(() => {
          window.scrollTo(0, savedPosition);
        });
      }
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [loading, tipsList, sourceId]); // Add loading and tipsList as dependencies

  const handleShowClick = useCallback((show) => {
    if (!show) return;
    
    // Save scroll position before navigating
    const scrollPosition = window.scrollY;
    pageStateStore.setState('tips', {
      sourceId,
      scrollPosition
    });

    navigate(`/show/${show.id}`, { 
      state: { 
        show,
        from: location.pathname,
        scrollPosition
      }
    });
  }, [navigate, location.pathname, sourceId]);

  const getFilteredShows = () => {
    if (!tipsList) return [];
    
    return tipsList.filter(show => {
      return activeStreamingFilter === 'all' || 
        (Array.isArray(show.streamingSources) && 
         show.streamingSources.some(source => 
           String(source).toLowerCase().trim() === activeStreamingFilter.toLowerCase().trim()
         ));
    });
  };

  // Helper function to show toast with auto-hide
  const showToast = (message, showSpinner = false) => {
    setToastState({
      show: true,
      message,
      showSpinner
    });

    // Only set timeout if not showing spinner
    if (!showSpinner) {
      setTimeout(() => {
        setToastState(prev => ({
          ...prev,
          show: false
        }));
      }, TOAST_DURATION);
    }
  };

  const getShowStatus = (show, lists) => {
    if (!lists || listsLoading) {
      console.log('Lists not ready:', { lists, listsLoading });
      return SHOW_STATUSES.NONE;
    }
    
    const showImdbId = show.imdb_id;
    const showId = show.id;
    
    console.log('Checking status for show:', { showId, showImdbId });
    console.log('Against lists:', {
      watchingList: lists.watchingList?.length,
      watchlist: lists.watchlist?.length,
      seenList: lists.seenList?.length,
      archivedList: lists.archivedList?.length
    });
    
    // Check both ID formats against each list
    const isInWatching = lists.watchingList?.some(item => 
      String(item.imdb_id) === String(showImdbId) || String(item.id) === String(showId)
    );
    const isInWatchlist = lists.watchlist?.some(item => 
      String(item.imdb_id) === String(showImdbId) || String(item.id) === String(showId)
    );
    const isInSeen = lists.seenList?.some(item => 
      String(item.imdb_id) === String(showImdbId) || String(item.id) === String(showId)
    );
    const isInArchived = lists.archivedList?.some(item => 
      String(item.imdb_id) === String(showImdbId) || String(item.id) === String(showId)
    );
    
    const status = isInWatching ? SHOW_STATUSES.WATCHING 
      : isInWatchlist ? SHOW_STATUSES.WATCHLIST
      : isInSeen ? SHOW_STATUSES.SEEN
      : isInArchived ? SHOW_STATUSES.ARCHIVED
      : SHOW_STATUSES.NONE;
      
    console.log('Determined status:', status);
    return status;
  };

  const handleWatchlistClick = (e, show) => {
    e.preventDefault();
    e.stopPropagation();
    handleStatusChange(show, SHOW_STATUSES.WATCHLIST);
  };

  const handleShare = async (e, show) => {
    e.stopPropagation();
    try {
      const shareUrl = `${window.location.origin}/show/${show.id}`;
      if (navigator.share) {
        await navigator.share({
          title: show.title,
          text: `Kolla in ${show.title}!`,
          url: shareUrl
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        showToast('Länk kopierad!');
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  const handleStreamingSourceClick = async (e, show) => {
    e.stopPropagation();
    if (!user) {
      navigate('/login');
      return;
    }

    setLoadingItems(prev => new Set(prev).add(show.id));
    
    try {
      // Fetch current streaming sources for the show
      const showDoc = await getDoc(doc(db, 'shows', show.id.toString()));
      if (showDoc.exists()) {
        const showData = showDoc.data();
        setStreamingSources(prev => ({
          ...prev,
          [show.id]: showData.streamingSources || []
        }));
      }
      
      setSelectedShowForSources(show);
      setShowStreamingModal(true);
    } catch (error) {
      console.error('Error fetching streaming sources:', error);
      showToast('Kunde inte hämta streamingtjänster');
    } finally {
      setLoadingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(show.id);
        return newSet;
      });
    }
  };

  const handleMarkAsSeen = async (e, show) => {
    e.stopPropagation();
    if (!user) {
      navigate('/login');
      return;
    }

    setLoadingItems(prev => new Set(prev).add(show.imdb_id));
    try {
      if (seenList.some(item => String(item.imdb_id) === String(show.imdb_id))) {
        await removeFromWatchlist(user.uid, show.imdb_id);
        setSeenList(prev => prev.filter(item => item.imdb_id !== show.imdb_id));
        showToast('Markerad som osedd');
      } else {
        await markShowAsSeen(user.uid, show);
        setSeenList(prev => [...prev, show]);
        showToast('Markerad som sedd');
      }
    } catch (error) {
      console.error('Error updating seen status:', error);
      showToast('Ett fel uppstod');
    } finally {
      setLoadingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(show.imdb_id);
        return newSet;
      });
    }
  };

  const handleArchiveShow = async (e, show) => {
    e.stopPropagation();
    if (!user) {
      navigate('/login');
      return;
    }

    const imdbId = String(show.imdb_id);
    if (!imdbId) {
      console.error('No IMDB ID found for show:', show);
      showToast('Ett fel uppstod');
      return;
    }

    setLoadingItems(prev => new Set(prev).add(imdbId));
    try {
      if (archivedList.some(item => String(item.imdb_id) === imdbId)) {
        await unarchiveShow(user.uid, imdbId);
        setArchivedList(prev => prev.filter(item => String(item.imdb_id) !== imdbId));
        showToast('Återställd från arkiv');
      } else {
        // Create a clean show object with only the necessary fields
        const showToArchive = {
          imdb_id: imdbId,
          title: show.title,
          year: show.year,
          poster: show.poster || '/images/default-poster.jpg',
          streamingSources: show.streamingSources || [],
          plot_overview: show.plot_overview || '',
          backdrop: show.backdrop || '',
          total_seasons: show.total_seasons || null,
          averageRating: show.averageRating || null,
          ratingCount: show.ratingCount || 0,
          addedAt: new Date()
        };
        await archiveShow(user.uid, showToArchive);
        setArchivedList(prev => [...prev, showToArchive]);
        showToast('Arkiverad');
      }
    } catch (error) {
      console.error('Error updating archive status:', error);
      showToast('Ett fel uppstod');
    } finally {
      setLoadingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(imdbId);
        return newSet;
      });
    }
  };

  const handleRatingClick = (e, show) => {
    e.stopPropagation();
    if (!user) {
      navigate('/login');
      return;
    }
    setSelectedShowForRating(show);
    setShowRatingOnlyModal(true);
  };

  const handleRatingSubmit = async (rating) => {
    if (!selectedShowForRating || !user) return;

    showToast('Sparar betyg...', true);

    try {
      // Add your rating submission logic here
      showToast('Betyg sparat!');
    } catch (error) {
      console.error('Error submitting rating:', error);
      showToast('Ett fel uppstod');
    }
    setShowRatingOnlyModal(false);
    setSelectedShowForRating(null);
  };

  const handleAddStreamingSource = async (source) => {
    if (!user || !selectedShowForSources) return;

    try {
      // Update local state immediately for better UX
      setStreamingSources(prev => ({
        ...prev,
        [selectedShowForSources.id]: [...(prev[selectedShowForSources.id] || []), source]
      }));

      // Update in database
      await updateDoc(doc(db, 'shows', selectedShowForSources.id.toString()), {
        streamingSources: arrayUnion(source),
        updatedAt: serverTimestamp()
      });

      // Update tipsList to reflect the new streaming source
      setTipsList(prevTips => prevTips.map(show => {
        if (show.id === selectedShowForSources.id) {
          return {
            ...show,
            streamingSources: [...(show.streamingSources || []), source]
          };
        }
        return show;
      }));

      showToast('Streamingtjänst tillagd!');
    } catch (error) {
      console.error('Error adding streaming source:', error);
      showToast('Kunde inte lägga till streamingtjänst');
    }
  };

  const handleRemoveStreamingSource = async (source) => {
    if (!user || !selectedShowForSources) return;

    try {
      // Update local state immediately for better UX
      setStreamingSources(prev => ({
        ...prev,
        [selectedShowForSources.id]: prev[selectedShowForSources.id].filter(s => s !== source)
      }));

      // Update in database
      await updateDoc(doc(db, 'shows', selectedShowForSources.id.toString()), {
        streamingSources: arrayRemove(source),
        updatedAt: serverTimestamp()
      });

      // Update tipsList to reflect the removed streaming source
      setTipsList(prevTips => prevTips.map(show => {
        if (show.id === selectedShowForSources.id) {
          return {
            ...show,
            streamingSources: show.streamingSources?.filter(s => s !== source) || []
          };
        }
        return show;
      }));

      showToast('Streamingtjänst borttagen');
    } catch (error) {
      console.error('Error removing streaming source:', error);
      showToast('Kunde inte ta bort streamingtjänst');
    }
  };

  useEffect(() => {
    const fetchUserLists = async () => {
      if (!user) return;

      try {
        const [seenShows, archivedShows] = await Promise.all([
          getSeenShows(user.uid),
          getArchivedShows(user.uid)
        ]);

        setSeenList(seenShows);
        setArchivedList(archivedShows);
      } catch (error) {
        console.error('Error fetching user lists:', error);
        setError('Failed to load user lists');
      }
    };

    fetchUserLists();
  }, [user]);

  // Update the useEffect to match Popular's implementation
  useEffect(() => {
    console.log('Setting up user lists subscription, user:', user?.uid);
    
    if (!user) {
      setListsLoading(false);
      setUserLists({ watchlist: [], seenList: [], watchingList: [], archivedList: [] });
      return;
    }

    setListsLoading(true);

    // Set up subscriptions for each list type
    const watchlistRef = collection(db, 'users', user.uid, 'watchlist');
    const watchingRef = collection(db, 'users', user.uid, 'watching');
    const seenRef = collection(db, 'users', user.uid, 'seen');
    const archivedRef = collection(db, 'users', user.uid, 'archived');

    const unsubscribeWatchlist = onSnapshot(watchlistRef, (snapshot) => {
      const watchlist = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUserLists(prev => ({ ...prev, watchlist }));
      console.log('Watchlist updated:', watchlist);
    });

    const unsubscribeWatching = onSnapshot(watchingRef, (snapshot) => {
      const watchingList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUserLists(prev => ({ ...prev, watchingList }));
      console.log('Watching list updated:', watchingList);
    });

    const unsubscribeSeen = onSnapshot(seenRef, (snapshot) => {
      const seenList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUserLists(prev => ({ ...prev, seenList }));
      console.log('Seen list updated:', seenList);
    });

    const unsubscribeArchived = onSnapshot(archivedRef, (snapshot) => {
      const archivedList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUserLists(prev => ({ ...prev, archivedList }));
      console.log('Archived list updated:', archivedList);
      setListsLoading(false);
    });

    return () => {
      unsubscribeWatchlist();
      unsubscribeWatching();
      unsubscribeSeen();
      unsubscribeArchived();
    };
  }, [user]);

  const ShowCardSkeleton = () => (
    <div className="show-card-skeleton">
      <div className="poster-skeleton"></div>
      <div className="content-skeleton">
        <div className="title-skeleton"></div>
        <div className="rating-skeleton"></div>
        {/* Add streaming sources skeleton */}
        <div className="streaming-sources-skeleton">
          <div className="streaming-source-item"></div>
          <div className="streaming-source-item"></div>
          <div className="streaming-source-item"></div>
        </div>
      </div>
    </div>
  );

  const FilterButtonsSkeleton = () => (
    <div className="tab-container">
      {[...Array(6)].map((_, i) => (
        <div 
          key={i} 
          className="tab-button-skeleton"
          style={{ 
            width: '90px',
            height: '36px',
            borderRadius: '18px',
            margin: '0 8px'
          }}
        ></div>
      ))}
    </div>
  );

  const TipsShowItemSkeleton = () => (
    <div className="tips-show-item-skeleton">
      {/* Show Card Skeleton */}
      <div className="show-card-skeleton">
        <div className="show-row-skeleton">
          <div className="show-info-skeleton">
            <div className="poster-skeleton"></div>
            <div className="show-details-skeleton">
              <div className="title-skeleton"></div>
              <div className="year-skeleton"></div>
              <div className="overview-skeleton">
                <div className="line"></div>
                <div className="line"></div>
              </div>
            </div>
          </div>
          <div className="show-actions-skeleton">
            <div className="action-button-skeleton"></div>
            <div className="action-button-skeleton"></div>
            <div className="action-button-skeleton"></div>
          </div>
        </div>
      </div>

      {/* Tips Show Review Skeleton */}
      <div className="review-skeleton">
        <div className="review-header-skeleton">
          <div className="reviewer-skeleton"></div>
          <div className="date-skeleton"></div>
        </div>
        <div className="review-content-skeleton">
          <div className="line"></div>
          <div className="line"></div>
          <div className="line"></div>
        </div>
      </div>
    </div>
  );

  const handleBackClick = () => {
    navigate('/tips/pre', { replace: true });
  };

  useEffect(() => {
    // Prevent default scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    return () => {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    };
  }, []);

  useEffect(() => {
    const handlePopState = (event) => {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    };
  }, []);

  const handleStatusClick = (event, show) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (!user) {
      navigate('/login');
      return;
    }

    const buttonRect = event.currentTarget.getBoundingClientRect();
    
    // Position menu to the left of the button by default
    let left = buttonRect.left - 220; // menu width (200px) + gap (20px)
    let top = buttonRect.top;
    
    // If not enough space on the left, position to the right
    if (left < 16) {
      left = buttonRect.right + 20;
    }
    
    // If not enough space on either side, position below
    if (left + 200 > window.innerWidth - 16) {
      left = buttonRect.left;
      top = buttonRect.bottom + 8;
    }
    
    setMenuPosition({ left, top });
    setSelectedShowForStatus(show);
    setCurrentShowStatus(getShowStatus(show, userLists));
    setShowStatusMenu(true);
  };

  const StatusMenu = ({ show, position, onClose, currentStatus, onStatusChange }) => {
    return createPortal(
      <>
        <div className="popular-status-menu-overlay" onClick={onClose} />
        <div 
          className="popular-status-menu" 
          style={{ 
            position: 'fixed',
            left: `${position.left}px`,
            top: `${position.top}px`
          }}
        >
          <button 
            className={`status-option ${currentStatus === SHOW_STATUSES.WATCHING ? 'active' : ''}`}
            onClick={() => onStatusChange(show, SHOW_STATUSES.WATCHING)}
          >
            <i className="fas fa-satellite-dish"></i>
            <span>Tittar på</span>
          </button>
          <button 
            className={`status-option ${currentStatus === SHOW_STATUSES.WATCHLIST ? 'active' : ''}`}
            onClick={() => onStatusChange(show, SHOW_STATUSES.WATCHLIST)}
          >
            <i className="fas fa-check"></i>
            <span>Ska se</span>
          </button>
          <button 
            className={`status-option ${currentStatus === SHOW_STATUSES.SEEN ? 'active' : ''}`}
            onClick={() => onStatusChange(show, SHOW_STATUSES.SEEN)}
          >
            <i className="fas fa-check-double"></i>
            <span>Har sett</span>
          </button>
          <button 
            className={`status-option ${currentStatus === SHOW_STATUSES.ARCHIVED ? 'active' : ''}`}
            onClick={() => onStatusChange(show, SHOW_STATUSES.ARCHIVED)}
          >
            <i className="fas fa-trash"></i>
            <span>Arkivera</span>
          </button>
        </div>
      </>,
      document.body
    );
  };

  const handleStatusChange = async (show, newStatus) => {
    if (!user) {
      navigate('/login');
      return;
    }

    setShowStatusMenu(false);
    setSelectedShowForStatus(null);

    try {
      // First change the status
      switch (newStatus) {
        case SHOW_STATUSES.WATCHING:
          await markShowAsWatching(user.uid, show);
          await addActivity(user.uid, 'status', show.id, show.title, 'watching');
          break;
        case SHOW_STATUSES.WATCHLIST:
          await addToWatchlist(user.uid, show);
          await addActivity(user.uid, 'status', show.id, show.title, 'watchlist');
          break;
        case SHOW_STATUSES.SEEN:
          await markShowAsSeen(user.uid, show);
          await addActivity(user.uid, 'status', show.id, show.title, 'seen');
          break;
        case SHOW_STATUSES.ARCHIVED:
          await archiveShow(user.uid, show);
          await addActivity(user.uid, 'status', show.id, show.title, 'archived');
          break;
        default:
          throw new Error(`Invalid status: ${newStatus}`);
      }
      
      setToast({
        message: getStatusMessage(newStatus),
        type: 'success'
      });

    } catch (error) {
      console.error('Error in handleStatusChange:', {
        error,
        errorMessage: error.message,
        show,
        newStatus
      });
      
      setToast({
        message: error.message || 'Ett fel uppstod vid statusändring',
        type: 'error'
      });
    }
  };

  const getStatusMessage = (status) => {
    switch (status) {
      case SHOW_STATUSES.WATCHING:
        return 'Tillagd i Tittar på!';
      case SHOW_STATUSES.WATCHLIST:
        return 'Tillagd i Ska se!';
      case SHOW_STATUSES.SEEN:
        return 'Markerad som sedd!';
      case SHOW_STATUSES.ARCHIVED:
        return 'Arkiverad!';
      default:
        return 'Status uppdaterad!';
    }
  };

  return (
    <div className="tips-container">
      <div className="tips-header">
        <button 
          className="back-button"
          onClick={handleBackClick}
        >
        ← Tillbaka
        </button>
      </div>

      {loading ? (
        <FilterButtonsSkeleton />
      ) : (
        <div className="tips-tab-container">
          <button
            className={`tips-tab-button ${activeStreamingFilter === 'all' ? 'active' : ''}`}
            onClick={() => setActiveStreamingFilter('all')}
          >
            Alla
          </button>
          {Array.from(availableStreamingSources)
            .filter(source => source !== 'all')
            .map(source => (
              <button
                key={source}
                className={`tips-tab-button ${activeStreamingFilter === source ? 'active' : ''}`}
                onClick={() => setActiveStreamingFilter(source)}
              >
                {source === 'netflix' ? 'Netflix' :
                 source === 'hbo' ? 'HBO' :
                 source === 'prime' ? 'Prime' :
                 source === 'disney' ? 'Disney+' :
                 source.charAt(0).toUpperCase() + source.slice(1)}
              </button>
            ))}
        </div>
      )}

      {error && <div className="error-message">{error}</div>}
  
      <div className="shows-container">
        {loading ? (
          // Show multiple skeleton items while loading
          [...Array(3)].map((_, index) => (
            <TipsShowItemSkeleton key={index} />
          ))
        ) : (
          getFilteredShows()?.map((show, index) => {
            const formattedShow = {
              ...show,
              id: show.imdb_id || show.id,
              firestore_id: show.id
            };

            const showStatus = listsLoading 
              ? SHOW_STATUSES.NONE 
              : getShowStatus(formattedShow, userLists);
            
            console.log('Rendering show:', { 
              id: formattedShow.id, 
              status: showStatus,
              listsLoading
            });

            return (
              <div 
                key={formattedShow.id || index} 
                className="tips-show-item"
              >
                <ShowCard
                  show={formattedShow}
                  hideRating={true}
                  hideIndex={true}
                  showStatus={showStatus}
                  isLoadingStatus={listsLoading}
                  showArchiveButton={false}
                  showSeenButton={false}
                  showRatingButton={false}
                  onStatusClick={(e) => handleStatusClick(e, formattedShow)}
                  onShareClick={(e) => handleShare(e, formattedShow)}
                  onStreamingSourceClick={(e) => handleStreamingSourceClick(e, formattedShow)}
                  onClick={() => handleShowClick(formattedShow)}
                />
                <TipsShowReview 
                  actors={show.actors}
                  creators={show.creators}
                  releaseDate={show.date}
                  review={show.review}
                  reviewer={show.reviewer}
                  reviewDate={show.reviewDate}
                />
                {/* Display streaming platform and availability date */}
                <div className="streaming-info">
                  <div className="streaming-source">
                    {show.streaming_source} 
                  </div>
              
                </div>
              </div>
            );
          })
        )}
      </div>

      {showRatingOnlyModal && selectedShowForRating && (
        <RatingOnlyModal
          show={selectedShowForRating}
          onClose={() => {
            setShowRatingOnlyModal(false);
            setSelectedShowForRating(null);
          }}
          onSubmit={handleRatingSubmit}
        />
      )}

      {showStreamingModal && selectedShowForSources && (
        <StreamingSourceModal
          show={selectedShowForSources}
          existingSources={selectedShowForSources.streamingSources || []}
          onClose={() => {
            setShowStreamingModal(false);
            setSelectedShowForSources(null);
          }}
          onAddSource={handleAddStreamingSource}
          onRemoveSource={handleRemoveStreamingSource}
        />
      )}

      {showStatusMenu && selectedShowForStatus && (
        <StatusMenu
          show={selectedShowForStatus}
          position={menuPosition}
          onClose={() => {
            setShowStatusMenu(false);
            setSelectedShowForStatus(null);
          }}
          currentStatus={currentShowStatus}
          onStatusChange={handleStatusChange}
        />
      )}

      {toastState.show && (
        <Toast 
          message={toastState.message}
          showSpinner={toastState.showSpinner}
          onClose={() => setToastState({ show: false, message: '', showSpinner: false })}
          duration={TOAST_DURATION}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={3000}
        />
      )}

      {getFilteredShows().length === 0 && (
        <div className="tips-empty-state">
          <p>Inget innehåll</p>
        </div>
      )}
    </div>
  );
}

export default TipsPage; 